import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Crop, RotateCw, Maximize2, Square, Smartphone, Monitor } from 'lucide-react';

const PRESETS = [
  { label: 'Free', icon: Maximize2, aspect: undefined },
  { label: '1:1', icon: Square, aspect: 1 },
  { label: '4:5', icon: Smartphone, aspect: 4 / 5 },
  { label: '9:16', icon: Smartphone, aspect: 9 / 16 },
  { label: '16:9', icon: Monitor, aspect: 16 / 9 },
];

function getCroppedCanvas(image, crop) {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    crop.x * scaleX, crop.y * scaleY,
    crop.width * scaleX, crop.height * scaleY,
    0, 0,
    canvas.width, canvas.height
  );
  return canvas;
}

export function ImageCropTool({ isOpen, onClose, imageSrc, onCropComplete }) {
  const [crop, setCrop] = useState({ unit: '%', width: 80, height: 80, x: 10, y: 10 });
  const [activePreset, setActivePreset] = useState(0);
  const imgRef = useRef(null);

  const handlePreset = useCallback((idx) => {
    setActivePreset(idx);
    const preset = PRESETS[idx];
    setCrop(prev => ({
      ...prev,
      aspect: preset.aspect,
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (!imgRef.current || !crop.width || !crop.height) return;
    const canvas = getCroppedCanvas(imgRef.current, crop);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'cropped-image.png', { type: 'image/png' });
        const previewUrl = URL.createObjectURL(blob);
        onCropComplete({ file, previewUrl });
      }
      onClose();
    }, 'image/png', 1);
  }, [crop, onClose, onCropComplete]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="image-crop-modal">
        <DialogTitle className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--brand-primary)' }}>
          <Crop className="w-4 h-4" /> Crop Image
        </DialogTitle>

        {/* Aspect Ratio Presets */}
        <div className="flex gap-2 mb-2">
          {PRESETS.map((preset, idx) => {
            const Icon = preset.icon;
            return (
              <button
                key={preset.label}
                onClick={() => handlePreset(idx)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  activePreset === idx ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={activePreset === idx ? { backgroundColor: 'var(--brand-primary)' } : {}}
                data-testid={`crop-preset-${preset.label.replace(':', '-')}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {preset.label}
              </button>
            );
          })}
        </div>

        {/* Crop Area */}
        <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-900 rounded-lg p-2 min-h-[300px] max-h-[55vh]">
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              aspect={PRESETS[activePreset].aspect}
              className="max-h-full"
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop"
                style={{ maxHeight: '50vh', maxWidth: '100%', objectFit: 'contain' }}
                data-testid="crop-image"
              />
            </ReactCrop>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} data-testid="crop-cancel-btn">Cancel</Button>
          <Button
            onClick={handleConfirm}
            className="text-white gap-1.5"
            style={{ backgroundColor: 'var(--brand-primary)' }}
            data-testid="crop-confirm-btn"
          >
            <Crop className="w-3.5 h-3.5" />
            Apply Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
